/* src/views/voyageurs/VoyageursAvisCtrl.jsx — L'ÉCRAN AVIS NEUF (lot 6).
   Maquette voyageurs-complet.jsx + les 3 décisions Dimitri (2026-08-31) :
   1. moyenne sur les avis NOTÉS seulement — compteur « N avis · M notés » ;
   2. sous-notes TOUJOURS affichées — « Airbnb ne les fournit pas encore
      sur cet avis. » quand il manque une catégorie ;
   3. filtres MAISON et CHAMBRE — chambre filtrée → « la page publique
      affiche X pour la maison entière » à côté du chiffre.
   Existant CONSERVÉ (contrat INVENTAIRE_VOYAGEURS §3) : lecture seule
   DGCCRF, épinglage (6 places d'affichage public + avertissement),
   Actualiser, pagination 50, badge langue, notes admin, copilote
   copier-jamais-envoyer (AvisResponseCopilot tel quel). */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../../supabase'
import AvisResponseCopilot from '../../tabs/logements/AvisResponseCopilot'

const SOUS_NOTES = [
  ['value', 'Rapport qualité-prix'],
  ['checkin', 'Arrivée'],
  ['accuracy', 'Conformité'],
  ['location', 'Emplacement'],
  ['cleanliness', 'Propreté'],
  ['communication', 'Communication'],
]
const PLACES_PUBLIQUES = 6
// ÉCHELLE DES NOTES validée Dimitri 2026-09-02 (prototype à jour) — UNE
// seule échelle partout : ≥4,8 bleu · 4,5–4,8 ambre · <4,5 terracotta.
// (Sur Airbnb l'écart 4,8/4,5 est énorme — se voit sans lire le chiffre.)
const tonNote = (v) => (Number(v) >= 4.8 ? '#87b8e6' : Number(v) >= 4.5 ? '#d4a24c' : '#b8412b')
const fr1 = (x) => Number(x).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
// Piège « vide vaut zéro » (déjà mordu ailleurs : Number(null) === 0) :
// une note absente n'est PAS un zéro — elle sort de la division (décision 1).
const notee = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v))

async function chargerTousLesAvis() {
  // 50/page côté serveur — on agrège TOUT (DGCCRF : la moyenne ne peut pas
  // écarter un avis ; seuls les SANS-NOTE sortent de la division, décision 1).
  const jeton = (await supabase.auth.getSession()).data?.session?.access_token
  const tous = []
  let page = 1
  for (;;) {
    const r = await fetch(`/api/sites?action=avis_admin_list&page=${page}`, {
      headers: { Authorization: `Bearer ${jeton}` },
    })
    const j = await r.json()
    if (!r.ok || !j?.ok) throw new Error(j?.error || `lecture avis (${r.status})`)
    const d = j.data || j
    // Blindage 2026-09-02 : une ligne nulle dans la réponse est écartée,
    // elle ne doit pas faire tomber l'écran (parité incident Demandes).
    tous.push(...(d.avis || []).filter(Boolean))
    const total = Number(d.total) || tous.length
    if (tous.length >= total || !(d.avis || []).length) return { avis: tous, total }
    page += 1
  }
}

function statsDe(liste) {
  const notes = liste.filter((a) => notee(a.rating)).map((a) => Number(a.rating))
  const moyenne = notes.length ? notes.reduce((s, v) => s + v, 0) / notes.length : null
  const sous = {}
  for (const [cle] of SOUS_NOTES) {
    const vals = liste.map((a) => a.category_ratings?.[cle]).filter(notee).map(Number)
    if (vals.length) sous[cle] = vals.reduce((s, v) => s + v, 0) / vals.length
  }
  return { total: liste.length, notes: notes.length, moyenne, sous }
}

const Barre = ({ valeur, largeur = '100%' }) => (
  <span style={{ width: largeur, display: 'block', height: 4, borderRadius: 2, background: '#eef2f6', overflow: 'hidden' }}>
    <span style={{ display: 'block', width: `${Math.max(0, Math.min(100, (valeur / 5) * 100))}%`, height: '100%', borderRadius: 2, background: tonNote(valeur) }} />
  </span>
)

export default function VoyageursAvisCtrl({ cfg, destinationAvisId, onDestinationConsommee }) {
  const [etat, setEtat] = useState({ chargement: true, erreur: null, avis: [] })
  const [filtre, setFiltre] = useState('tous') // 'tous' | 'maison' | lid chambre
  const [feuille, setFeuille] = useState(null) // avis en réponse
  const [erreurPin, setErreurPin] = useState(null)

  const noms = useMemo(() => {
    const m = new Map()
    for (const p of cfg?.props || []) for (const l of p.logs || []) {
      m.set(l.id, l.nom)
      for (const ch of l.chambres || []) m.set(ch.id, ch.nom)
    }
    return m
  }, [cfg])

  const recharger = useCallback(async () => {
    setEtat((e) => ({ ...e, chargement: true, erreur: null }))
    try {
      const { avis } = await chargerTousLesAvis()
      setEtat({ chargement: false, erreur: null, avis })
    } catch (e) {
      setEtat({ chargement: false, erreur: e.message, avis: [] })
    }
  }, [])
  useEffect(() => { recharger() }, [recharger])

  // Arrivée ciblée depuis le bandeau (dépliage) : défiler jusqu'à l'avis.
  useEffect(() => {
    if (!destinationAvisId || etat.chargement) return
    const el = document.querySelector(`[data-avis-id="${destinationAvisId}"]`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    if (onDestinationConsommee) onDestinationConsommee()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationAvisId, etat.chargement])

  // Décision Dimitri 2026-09-04 : la MOYENNE ne porte que les logements du
  // parc (« un logement fermé n'a pas d'avis à moi ») — les avis d'un
  // logement retiré restent lisibles mais sortent des statistiques, et une
  // ligne le dit.
  const tousAvisBruts = etat.avis
  const tousAvis = tousAvisBruts.filter((a) => noms.has(a.logement_id))
  const avisHorsParc = tousAvisBruts.length - tousAvis.length
  const visibles = filtre === 'tous' ? tousAvis : tousAvis.filter((a) => a.logement_id === filtre || (filtre === 'maison' && String(a.logement_id).startsWith('lc')))
  const stats = statsDe(visibles)
  const statsMaison = statsDe(tousAvis.filter((a) => String(a.logement_id).startsWith('lc')))
  const filtreEstChambre = filtre !== 'tous' && filtre !== 'maison'
  const sources = [...new Set(tousAvis.map((a) => a.source))]
  const epingles = tousAvis.filter((a) => a.pinned).length

  const basculerPin = async (a) => {
    setErreurPin(null)
    const voulu = !a.pinned
    setEtat((e) => ({ ...e, avis: e.avis.map((x) => (x.id === a.id ? { ...x, pinned: voulu } : x)) }))
    try {
      const jeton = (await supabase.auth.getSession()).data?.session?.access_token
      const r = await fetch('/api/sites?action=avis_set_pinned', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jeton}` },
        body: JSON.stringify({ avis_id: a.id, pinned: voulu }),
      })
      if (!r.ok) throw new Error(`mise en avant refusée (${r.status})`)
    } catch (e) {
      setEtat((et) => ({ ...et, avis: et.avis.map((x) => (x.id === a.id ? { ...x, pinned: a.pinned } : x)) }))
      setErreurPin(e.message)
    }
  }

  const chips = useMemo(() => {
    const lids = [...new Set(tousAvis.map((a) => a.logement_id))]
    return [
      { id: 'tous', nom: 'Tout le parc', n: tousAvis.length },
      { id: 'maison', nom: 'Maison Zen (la maison)', n: tousAvis.filter((a) => String(a.logement_id).startsWith('lc')).length },
      ...lids.map((lid) => ({ id: lid, nom: noms.get(lid) || lid, n: tousAvis.filter((a) => a.logement_id === lid).length })),
    ]
  }, [tousAvis, noms])

  return (
    <div className="page" data-testid="voy-avis">
      <div className="info">Lecture seule du texte — la loi interdit de trier ou de masquer les avis. Ce que vous réglez ici est la <b>mise en avant sur la page publique</b> ({PLACES_PUBLIQUES} places), jamais le contenu ni la note.</div>
      {avisHorsParc > 0 && (
        <p className="note" data-testid="avis-hors-parc">{avisHorsParc} avis appartiennent à des logements retirés du parc — ils ne comptent pas dans vos moyennes.</p>
      )}

      {/* ── Synthèse (décisions 1 et 3) ── */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 14, marginBottom: 12, boxShadow: '0 1px 3px rgba(35,50,64,.08),0 0 0 1px rgba(35,50,64,.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 13 }}>
          <span style={{ width: 52, height: 52, borderRadius: 14, background: stats.moyenne != null ? tonNote(stats.moyenne) : '#5c6773', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <b style={{ fontSize: 19, fontWeight: 600, color: '#fff', lineHeight: 1 }} data-testid="avis-moyenne">{stats.moyenne != null ? fr1(stats.moyenne) : '—'}</b>
            <span style={{ fontSize: 8, color: 'rgba(255,255,255,.7)', marginTop: 1 }}>sur 5</span>
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 12.5, fontWeight: 600, color: '#2e3d4d', margin: 0 }} data-testid="avis-compteur">
              {/* Pas encore lu (ou lecture ratée) : « — », jamais « 0 avis ». */}
              {etat.chargement || etat.erreur ? '— avis' : `${stats.total} avis · ${stats.notes} noté${stats.notes > 1 ? 's' : ''}`}
              {!etat.chargement && !etat.erreur && stats.total !== stats.notes ? ` — la note ne compte que les avis notés` : ''}
            </p>
            <p style={{ fontSize: 10.5, color: '#5a6470', margin: '3px 0 0', lineHeight: 1.45 }}>
              {sources.length === 1 && sources[0] === 'airbnb' ? "tous d'Airbnb — les autres plateformes n'en ont pas encore produit" : sources.join(' · ')}
              {' · '}{etat.chargement || etat.erreur ? '—' : epingles} en avant{epingles > PLACES_PUBLIQUES ? ` — seuls ${PLACES_PUBLIQUES} sortent sur la page publique` : ` (${PLACES_PUBLIQUES} places publiques)`}
            </p>
            {filtreEstChambre && statsMaison.moyenne != null && (
              <p style={{ fontSize: 10.5, color: '#9295a0', margin: '3px 0 0' }} data-testid="avis-contexte-maison">
                la page publique affiche {fr1(statsMaison.moyenne)} pour la maison entière
              </p>
            )}
          </div>
          <button className="bt bt-s" onClick={recharger} style={{ flexShrink: 0 }}>Actualiser</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(112px,1fr))', gap: '7px 14px' }}>
          {SOUS_NOTES.map(([cle, libelle]) => (
            <div key={cle}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginBottom: 4 }}>
                <b style={{ fontSize: 13, color: stats.sous[cle] != null ? tonNote(stats.sous[cle]) : '#9295a0', lineHeight: 1 }}>{stats.sous[cle] != null ? fr1(stats.sous[cle]) : '—'}</b>
                <span style={{ flex: 1, fontSize: 9.5, color: '#5a6470', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{libelle}</span>
              </div>
              {stats.sous[cle] != null ? <Barre valeur={stats.sous[cle]} /> : <span style={{ fontSize: 9, color: '#9295a0' }}>pas encore fournie</span>}
            </div>
          ))}
        </div>
        <p style={{ fontSize: 9.5, color: '#5a6470', margin: '10px 0 0' }}>
          <span style={{display:'inline-block',width:8,height:8,borderRadius:4,background:'#87b8e6',marginRight:4}}/>4,8 et plus
          <span style={{display:'inline-block',width:8,height:8,borderRadius:4,background:'#d4a24c',margin:'0 4px 0 12px'}}/>entre 4,5 et 4,8
          <span style={{display:'inline-block',width:8,height:8,borderRadius:4,background:'#b8412b',margin:'0 4px 0 12px'}}/>sous 4,5 — là où un voyageur hésite
        </p>
      </div>

      {/* ── Filtres maison + chambres (décision 3) ── */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 11 }}>
        {chips.map((c) => (
          <button key={c.id} onClick={() => setFiltre(c.id)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 36, padding: '0 12px', borderRadius: 9, fontSize: 11.5, background: filtre === c.id ? '#3d5166' : '#fff', border: `1px solid ${filtre === c.id ? '#3d5166' : '#d5e3ee'}`, color: filtre === c.id ? '#fff' : '#5a6470', fontWeight: filtre === c.id ? 600 : 400, cursor: 'pointer' }}>
            {c.nom}
            <span style={{ minWidth: 16, height: 16, borderRadius: 8, background: filtre === c.id ? 'rgba(255,255,255,.24)' : '#eef2f6', color: filtre === c.id ? '#fff' : '#5c6773', fontSize: 9, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{c.n}</span>
          </button>
        ))}
      </div>

      {erreurPin && <div className="warn" data-testid="avis-pin-erreur">La mise en avant n'a pas pu être enregistrée ({erreurPin}) — l'écran est revenu à l'état réel.</div>}
      {etat.chargement && <p className="note">Chargement des avis…</p>}
      {etat.erreur && <div className="warn">Les avis n'ont pas pu être chargés : {etat.erreur}. <button className="bt bt-s" onClick={recharger}>Réessayer</button></div>}
      {!etat.chargement && !etat.erreur && visibles.length === 0 && <p className="note">Aucun avis pour ce filtre.</p>}

      {/* ── Cartes ── */}
      {visibles.map((a) => (
        <div key={a.id} data-avis-id={a.id} className="carte" style={{ margin: '0 0 9px', padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: notee(a.rating) ? tonNote(a.rating) : '#5c6773' }}>
            <span style={{ width: 36, height: 36, borderRadius: 10, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <b style={{ fontSize: 14, color: notee(a.rating) ? tonNote(a.rating) : '#9295a0' }}>{notee(a.rating) ? fr1(a.rating) : '—'}</b>
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: '#fff' }}>{a.guest_name || 'Anonyme'} · {a.review_date}</span>
              <span style={{ display: 'block', fontSize: 10, color: 'rgba(255,255,255,.75)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {noms.get(a.logement_id) || a.logement_id} · {a.source}{a.locale && a.locale !== 'fr' ? ` · ${String(a.locale).toUpperCase()}` : ''}{!notee(a.rating) ? ' · sans note' : ''}
              </span>
            </span>
            {a.pinned
              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,.24)', borderRadius: 8, padding: '3px 9px', fontSize: 10, fontWeight: 600, color: '#fff', flexShrink: 0 }}>★ sur la page</span>
              : <span style={{ fontSize: 15, color: 'rgba(255,255,255,.42)', flexShrink: 0 }}>☆</span>}
          </div>
          <div style={{ padding: '11px 12px' }}>
            <p style={{ fontSize: 12, color: '#2e3d4d', margin: 0, lineHeight: 1.6 }}>{a.review_text}</p>
            {/* Toutes les sous-notes absentes → UNE ligne (validé Dimitri :
                six répétitions étaient bavardes). */}
            {SOUS_NOTES.every(([cle]) => !notee(a.category_ratings?.[cle])) ? (
              <p style={{ fontSize: 10, color: '#9295a0', margin: '11px 0 0' }}>Airbnb n'a fourni aucune sous-note sur cet avis.</p>
            ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(104px,1fr))', gap: '4px 12px', marginTop: 11 }}>
              {SOUS_NOTES.map(([cle, libelle]) => {
                const v = a.category_ratings?.[cle]
                return (
                  <div key={cle} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 9.5, color: '#5a6470', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{libelle}</span>
                    {notee(v)
                      ? <><span style={{ width: 34, flexShrink: 0 }}><Barre valeur={Number(v)} largeur="34px" /></span>
                          <b style={{ fontSize: 10, color: tonNote(v), flexShrink: 0, width: 8 }}>{v}</b></>
                      : <span style={{ fontSize: 9, color: '#9295a0', flexShrink: 0 }} title="Décision 2 : catégorie manquante ANNONCÉE, jamais masquée">Airbnb ne la fournit pas encore</span>}
                  </div>
                )
              })}
            </div>
            )}
            {a.admin_notes && <p className="note" style={{ margin: '9px 0 0' }}>Note interne : {a.admin_notes}</p>}
            <div className="act">
              {a.status === 'auto_approved' && (
                <button className="bt bt-s" onClick={() => basculerPin(a)}>{a.pinned ? '★ Retirer de la page' : '☆ Mettre sur la page'}</button>
              )}
              <button className="bt bt-s" onClick={() => setFeuille(a)}>Préparer une réponse</button>
            </div>
          </div>
        </div>
      ))}
      <p className="note">La loi interdit de trier ou de masquer les avis. Ce que vous réglez ici est la mise en avant sur la page publique, jamais le contenu.</p>

      {/* ── Feuille réponse : le copilote existant, copier jamais envoyer ── */}
      {feuille && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={() => setFeuille(null)}>
          <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 640, maxHeight: '82vh', overflowY: 'auto', padding: 16 }} onClick={(e) => e.stopPropagation()}>
            <div className="carte" style={{ margin: 0, background: '#f6f8fa' }}>
              <p style={{ fontSize: 11.5, color: '#2e3d4d', margin: 0, lineHeight: 1.6 }}>{feuille.review_text}</p>
              <p className="note">{feuille.review_date} · {notee(feuille.rating) ? `${fr1(feuille.rating)} sur 5` : 'sans note'}</p>
            </div>
            <AvisResponseCopilot avis={feuille} />
            <div className="act"><button className="bt bt-s" onClick={() => setFeuille(null)}>Fermer</button></div>
          </div>
        </div>
      )}
    </div>
  )
}
